import type { ResolutionChangeRequest, ResolutionChangeStatus, ResolutionObservation } from '@playcastdotio/PeerCore';
import { RESOLUTION_HOST_VERIFY_MS, RESOLUTION_RECEIVER_VERIFY_MS } from '@playcastdotio/PeerCore';

/** Effect completion and measured frames are deliberately separate acknowledgements. */
export class ResolutionChangeVerifier {
  private active?: ResolutionChangeRequest;
  private stage?: ResolutionChangeStatus['stage'];
  private senderVerifiedAt?: number;
  private sawSender = false;
  private applied = false;
  private readonly sequences = new Map<string, number>();

  public request(request: ResolutionChangeRequest): ResolutionChangeStatus {
    this.reset();
    this.active = request;
    return this.status('requested', request.requestedAtMs);
  }

  public hostApplied(requestId: string, nowMs: number): ResolutionChangeStatus {
    if (requestId !== this.active?.requestId) return this.superseded(requestId);
    if (this.stage === 'failed' || this.stage === 'host-timeout') return this.status(this.stage, nowMs);
    if (nowMs - this.active.requestedAtMs >= RESOLUTION_HOST_VERIFY_MS && this.senderVerifiedAt === undefined)
      return this.status('host-timeout', nowMs, 'missing-new-sender-frames');
    this.applied = true;
    if (this.senderVerifiedAt !== undefined) return this.status(this.stage!, nowMs);
    return this.status('host-applied', nowMs);
  }

  public observe(observation: ResolutionObservation, nowMs: number): ResolutionChangeStatus | undefined {
    const request = this.active;
    if (!request || observation.peerGeneration !== request.peerGeneration || observation.pathEpoch !== request.pathEpoch) return;
    if (!this.applied || this.stage === 'failed' || this.stage === 'host-timeout') return;
    if (this.senderVerifiedAt === undefined && nowMs - request.requestedAtMs >= RESOLUTION_HOST_VERIFY_MS)
      return this.tick(nowMs);
    if (observation.freshness.state !== 'fresh' || !observation.observedAfterRequest) return;
    if (observation.observationSequence <= (this.sequences.get(observation.source) ?? -1)) return;
    this.sequences.set(observation.source, observation.observationSequence);
    if (observation.source === 'sender') this.sawSender = true;
    if (observation.width <= 0 || observation.height <= 0 || observation.width > request.maxWidth || observation.height > request.maxHeight) return;
    if (observation.source === 'sender' && this.senderVerifiedAt === undefined) {
      this.senderVerifiedAt = nowMs;
      return this.status('sender-verified', nowMs);
    }
    if (observation.source === 'receiver' && this.senderVerifiedAt !== undefined && this.stage !== 'receiver-verified') {
      return this.status('receiver-verified', nowMs);
    }
    return;
  }

  public fail(requestId: string, reason: string, nowMs: number): ResolutionChangeStatus {
    if (requestId !== this.active?.requestId) return this.superseded(requestId);
    return this.status('failed', nowMs, reason.slice(0, 160) || 'application-failed');
  }

  public tick(nowMs: number): ResolutionChangeStatus | undefined {
    if (!this.active || this.stage === 'receiver-verified' || this.stage === 'failed') return;
    if (this.senderVerifiedAt === undefined && nowMs - this.active.requestedAtMs >= RESOLUTION_HOST_VERIFY_MS && this.stage !== 'host-timeout') {
      return this.status('host-timeout', nowMs, this.sawSender ? 'sender-dimensions-exceed-limit' : 'missing-new-sender-frames');
    }
    if (this.senderVerifiedAt !== undefined && nowMs - this.senderVerifiedAt >= RESOLUTION_RECEIVER_VERIFY_MS && this.stage !== 'receiver-unverified') {
      return this.status('receiver-unverified', nowMs, 'missing-trusted-receiver-dimensions');
    }
    return;
  }

  public reset(): void {
    this.active = undefined;
    this.stage = undefined;
    this.senderVerifiedAt = undefined;
    this.sawSender = false;
    this.applied = false;
    this.sequences.clear();
  }

  private status(stage: ResolutionChangeStatus['stage'], nowMs: number, reason?: string): ResolutionChangeStatus {
    this.stage = stage;
    return { requestId: this.active!.requestId, stage, elapsedMs: Math.max(0, nowMs - this.active!.requestedAtMs), ...(reason ? { reason } : {}) };
  }

  private superseded(requestId: string): ResolutionChangeStatus {
    return { requestId, stage: 'superseded', elapsedMs: 0 };
  }
}
